/**
 * features/finance/Bursaries.tsx
 * ------------------------------------------------------------------
 * Bursary schemes, and the per-pupil awards made under them.
 *
 * An award is a percentage of TUITION for one pupil for one year, applied
 * termly in the billing run AFTER the sibling discount. It reduces tuition
 * only — an activity levy or an exam fee is still payable, because a bursary
 * is help with schooling, not a blanket exemption.
 *
 * A finance manager awards; the Head approves. Nothing reaches a bill until
 * it does: the billing run reads only approved awards, and Firestore rules
 * refuse a manager's write that would leave an award approved. Editing an
 * approved award therefore knocks it back to pending, which is the point —
 * otherwise a 5% award could be approved and then quietly raised to 100%.
 *
 * NO SCHEMES ARE SEEDED. The schemes a school runs are the school's own, and
 * a helpfully pre-filled list is how another institution's sponsor ends up
 * named in this one's records. The bursar creates them.
 * ------------------------------------------------------------------ */
import { useState, useMemo } from 'react';
import { Award, Search, X, Plus, Trash2, Percent, Tags } from 'lucide-react';
import { useData } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { getPermissions } from '@/lib/permissions';
import { auditFromContext } from '@/lib/audit';
import { Spinner } from '@/components/ui/primitives';
import { SoftCard, StatChip, glassFieldClass, btnPrimary, btnGhost } from '@/features/finance/ui';
import { finance } from '@/features/finance/financeWrite';
import { useFinanceData } from '@/features/finance/useFinanceData';
import { feeCaseApproved } from '@/lib/financeCore';
import { FeeCaseBadge, FeeCaseDecision, approvalPatch } from '@/features/finance/FeeCaseApproval';
import type { Student } from '@/types';

const CURRENT_YEAR = String(new Date().getFullYear());

export default function Bursaries() {
  const { activeStudents, loading } = useData();
  const { bursaryTypes, bursaryAwards } = useFinanceData();
  const { userData, userRoles } = useAuth();
  const perms = getPermissions(userRoles);

  const [year, setYear] = useState(CURRENT_YEAR);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  // The add-an-award row.
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<Student | null>(null);
  const [typeId, setTypeId] = useState('');
  const [pct, setPct] = useState('100');

  // The new-scheme row.
  const [showScheme, setShowScheme] = useState(false);
  const [schemeName, setSchemeName] = useState('');
  const [schemeDesc, setSchemeDesc] = useState('');

  const activeTypes = useMemo(
    () => bursaryTypes.filter((t) => t.active !== false).sort((a, b) => a.name.localeCompare(b.name)),
    [bursaryTypes],
  );
  const typeName = (id: string) => bursaryTypes.find((t) => t.id === id)?.name ?? id;

  const awards = useMemo(
    () => bursaryAwards
      .filter((a) => a.year === year)
      .sort((a, b) => {
        // Anything awaiting a decision first — it is the only thing on this
        // screen that someone still has to act on.
        const rank = (s?: string) => ((s ?? 'pending') === 'pending' ? 0 : 1);
        return rank(a.status) - rank(b.status) || (a.studentName || '').localeCompare(b.studentName || '');
      }),
    [bursaryAwards, year],
  );

  const approvedCount = awards.filter((a) => feeCaseApproved(a)).length;
  const pendingCount = awards.filter((a) => (a.status ?? 'pending') === 'pending').length;

  const matches = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (s.length < 2) return [];
    return activeStudents
      .filter((x) => x.fullName.toLowerCase().includes(s) || (x.admissionNumber ?? '').toLowerCase().includes(s))
      .slice(0, 8);
  }, [activeStudents, q]);

  async function addScheme() {
    const name = schemeName.trim();
    if (!name) { setMsg('Give the scheme a name.'); return; }
    setBusy(true); setMsg(null);
    try {
      const id = `bt_${Date.now().toString(36)}`;
      await finance.bursaryTypes.set(id, { id, name, description: schemeDesc.trim() || undefined, active: true });
      auditFromContext(userData, 'bursary_type_created', 'finance', {
        targetId: id, targetType: 'setting', targetName: name, details: { name },
      });
      setSchemeName(''); setSchemeDesc(''); setShowScheme(false);
      setMsg(`Scheme "${name}" added.`);
    } finally { setBusy(false); }
  }

  async function addAward() {
    if (!picked || !typeId) { setMsg('Pick a pupil and a scheme.'); return; }
    const percent = Math.max(0, Math.min(100, Math.round(Number(pct) || 0)));
    if (percent <= 0) { setMsg('Enter a percentage between 1 and 100.'); return; }
    setBusy(true); setMsg(null);
    try {
      const id = `${year}_${picked.id}_${typeId}`;
      await finance.bursaryAwards.set(id, {
        id, studentId: picked.id, studentName: picked.fullName,
        bursaryTypeId: typeId, bursaryTypeName: typeName(typeId),
        year, percent,
        status: 'pending',
        createdBy: userData?.uid, createdByName: userData?.displayName,
      });
      auditFromContext(userData, 'bursary_awarded', 'finance', {
        targetId: picked.id, targetType: 'student', targetName: picked.fullName,
        details: { year, type: typeName(typeId), percent },
      });
      setPicked(null); setQ(''); setPct('100');
      setMsg('Award recorded — it affects no bill until it is approved.');
    } finally { setBusy(false); }
  }

  async function decide(id: string, decision: 'approved' | 'rejected', name?: string, reason?: string) {
    if (busy) return;
    setBusy(true);
    try {
      await finance.bursaryAwards.update(id, approvalPatch(decision, userData, reason));
      auditFromContext(userData, `bursary_${decision}`, 'finance', {
        targetId: id, targetType: 'student', targetName: name, details: { year, reason },
      });
    } finally { setBusy(false); }
  }

  async function removeAward(id: string, studentId: string, name?: string, type?: string, percent?: number) {
    if (busy) return;
    setBusy(true);
    try {
      await finance.bursaryAwards.delete(id);
      auditFromContext(userData, 'bursary_removed', 'finance', {
        targetId: studentId, targetType: 'student', targetName: name,
        details: { year, type, percent },
      });
    } finally { setBusy(false); }
  }

  if (loading) return <div className="flex justify-center py-16"><Spinner label="Loading pupils…" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Bursaries</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            A percentage of tuition, taken after the sibling discount and applied termly.
            Other charges stay payable.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm shrink-0">
          <span className="text-gray-500 dark:text-gray-400 text-xs">Year</span>
          <select value={year} onChange={(e) => setYear(e.target.value)} className={glassFieldClass('py-1.5 w-28 text-sm')}>
            {[0, 1, -1].map((d) => String(Number(CURRENT_YEAR) + d)).map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <StatChip tone="emerald" value={String(approvedCount)} label={`Approved for ${year}`} icon={Award} />
        <StatChip tone="warn" value={String(pendingCount)} label="Awaiting approval" />
        <StatChip tone="navy" value={String(activeTypes.length)} label="Schemes" icon={Tags} />
      </div>

      {msg && <p className="text-xs text-gray-600 dark:text-gray-300">{msg}</p>}

      {/* ── schemes ─────────────────────────────────────────────── */}
      {perms.canManageFinanceConfig && (
        <SoftCard pad="p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">Schemes</h3>
              <p className="text-xs text-gray-400 mt-0.5">
                {activeTypes.length === 0
                  ? 'None yet. Create the schemes this school actually runs — nothing is pre-filled.'
                  : activeTypes.map((t) => t.name).join(' · ')}
              </p>
            </div>
            <button type="button" onClick={() => setShowScheme((v) => !v)} className={btnGhost}>
              <Plus size={14} /> New scheme
            </button>
          </div>

          {showScheme && (
            <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_2fr_auto]">
              <input
                value={schemeName}
                onChange={(e) => setSchemeName(e.target.value)}
                placeholder="Scheme name"
                className={glassFieldClass('py-1.5 text-sm')}
              />
              <input
                value={schemeDesc}
                onChange={(e) => setSchemeDesc(e.target.value)}
                placeholder="What it is for (optional)"
                className={glassFieldClass('py-1.5 text-sm')}
              />
              <button type="button" onClick={addScheme} disabled={busy} className={btnPrimary}>
                <Plus size={14} /> Add
              </button>
            </div>
          )}
        </SoftCard>
      )}

      {/* ── award a bursary ─────────────────────────────────────── */}
      {perms.canManageFinanceConfig && activeTypes.length > 0 && (
        <SoftCard pad="p-4">
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm mb-3">Award a bursary</h3>
          <div className="grid gap-2 sm:grid-cols-[2fr_1.5fr_6rem_auto] items-start">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-2.5 text-gray-400" />
              <input
                value={picked ? picked.fullName : q}
                onChange={(e) => { setPicked(null); setQ(e.target.value); }}
                placeholder="Search a pupil…"
                className={glassFieldClass('pl-9 py-1.5 text-sm')}
              />
              {!picked && matches.length > 0 && (
                <div className="absolute z-10 mt-1 w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 shadow-lg max-h-56 overflow-y-auto">
                  {matches.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => { setPicked(s); setQ(''); }}
                      className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      {s.fullName}
                      {s.admissionNumber && <span className="text-[11px] text-gray-400 ml-2">{s.admissionNumber}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <select value={typeId} onChange={(e) => setTypeId(e.target.value)} className={glassFieldClass('py-1.5 text-sm')}>
              <option value="">Scheme…</option>
              {activeTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>

            <div className="relative">
              <input
                value={pct}
                onChange={(e) => setPct(e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
                placeholder="100"
                inputMode="numeric"
                className={glassFieldClass('py-1.5 text-sm text-right pr-7')}
              />
              <Percent size={13} className="absolute right-2.5 top-2.5 text-gray-400" />
            </div>

            <button type="button" onClick={addAward} disabled={busy || !picked || !typeId} className={btnPrimary}>
              <Plus size={14} /> Award
            </button>
          </div>
        </SoftCard>
      )}

      {/* ── the awards ──────────────────────────────────────────── */}
      <SoftCard pad="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800/50 text-left text-gray-500 dark:text-gray-400">
              <tr>
                <th className="px-4 py-2.5 font-medium">Pupil</th>
                <th className="px-4 py-2.5 font-medium">Scheme</th>
                <th className="px-4 py-2.5 font-medium text-right">% of tuition</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
                <th className="px-4 py-2.5 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {awards.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    No bursaries awarded for {year}.
                  </td>
                </tr>
              )}
              {awards.map((a) => (
                <tr key={a.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/40">
                  <td className="px-4 py-2.5 font-medium text-gray-800 dark:text-gray-100">
                    {a.studentName || a.studentId}
                    {a.createdByName && <span className="text-[11px] text-gray-400 ml-2">by {a.createdByName}</span>}
                  </td>
                  <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">{a.bursaryTypeName || typeName(a.bursaryTypeId)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-gray-800 dark:text-gray-100">{a.percent}%</td>
                  <td className="px-4 py-2.5">
                    <FeeCaseBadge status={a.status} approvedByName={a.approvedByName} rejectedReason={a.rejectedReason} />
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end gap-2">
                      {perms.canApproveFeeCases && (
                        <FeeCaseDecision
                          status={a.status}
                          busy={busy}
                          onApprove={() => decide(a.id, 'approved', a.studentName)}
                          onReject={(reason) => decide(a.id, 'rejected', a.studentName, reason)}
                        />
                      )}
                      {perms.canManageFinanceConfig && (
                        <button
                          type="button"
                          onClick={() => removeAward(a.id, a.studentId, a.studentName, a.bursaryTypeName, a.percent)}
                          disabled={busy}
                          className="p-1 rounded text-gray-400 hover:text-red-600 disabled:opacity-40"
                          title="Remove this award"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SoftCard>

      <p className="text-[11px] text-gray-400 flex items-start gap-1.5">
        <X size={12} className="mt-0.5 shrink-0 opacity-0" aria-hidden />
        Only approved awards reach a billing run. Where a pupil holds more than one, the largest applies — they do not add up.
      </p>
    </div>
  );
}
